/** @format */

import React, { useContext, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import { useTranslation } from "react-i18next";
import { roles } from "../../Config/Roles";
import { AuthContext } from "../../Contexts/AuthContext";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { digitalContentService } from "../../Services/digitalContent.services";
import ActionModal from "../Common/ActionModal";
import ResultModal from "../Common/ResultModal";
import SpinnerLoading from "../Common/SpinnerLoading";
import AddDigitalContent from "./AddDigitalContent";
import UpdateDigitalContent from "./UpdateDigitalContent";

const Index = () => {
  const { t: translator } = useTranslation();
  const { updateLocName } = useContext(GlobalContext);
  const { roleId } = useContext(AuthContext);
  const { master, admin, support } = roles;

  const isActionAllowed = [master, admin, support].includes(parseInt(roleId));

  const { SearchBar } = Search;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [updateData, setUpdateData] = useState(null);
  const [update, setUpdate] = useState(new Date());

  const [resultModal, setResultModal] = useState({
    show: false,
    title: "",
    text: "",
    btnClass: "",
  });

  const [actionModal, setActionModal] = useState({
    show: false,
    title: "",
    text: "",
    confirmBtn: "",
  });

  useEffect(() => updateLocName("digital-content"), []);
  useEffect(() => {
    setIsLoading(true);
    digitalContentService
      .getAll()
      .then((res) => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch((err) => console.log(err));
  }, [update]);

  const onClickDelete = (rowId) => {
    setActionModal({
      show: true,
      title: "DigitalContent-Delete-Title",
      text: "DigitalContent-Delete-Text",
      confirmBtn: "DigitalContent-Delete-Btn",
      confirmAction: () => confirmDelete(rowId),
    });
  };

  const hrDateFormatter = new Intl.DateTimeFormat('hr-HR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  function dateFormatter(cell) {
    if (!cell) return '';
    const date = new Date(cell);
    return isNaN(date) ? cell : hrDateFormatter.format(date);
  }

function contentFormatter(cell, row) {
    if (row.contentType === 'image') {
      return (
        <img
          src={cell}
          alt='preview'
          style={{ maxHeight: 60, maxWidth: 100, objectFit: 'contain' }}
        />
      );
    }
    if (row.contentType === 'video') {
      return (
        <video
          src={cell}
          style={{ maxHeight: 60, maxWidth: 100 }}
          controls
        />
      );
    }
    return <span>{cell}</span>;
  }

  function actionFormatter(rowId) {
    return (
      <div
        className="action"
        onClick={(e) => {
          e.stopPropagation();
          onClickDelete(rowId);
        }}
      >
        <span className="icon icon-cancel" style={{ color: "#c00" }}></span>
      </div>
    );
  }

  const columns = [
    {
      dataField: "digitalContentId",
      text: "",
      headerStyle: () => ({ width: "50px", textAlign: "center" }),
      formatter: actionFormatter,
      hidden: !isActionAllowed,
    },
    {
      dataField: "content",
      text: translator("DigitalContent-Content"),
      formatter: contentFormatter,
    },
    {
      dataField: "duration",
      text: translator("DigitalContent-Duration"),
      headerStyle: () => ({ width: "90px" }),
    },
    {
      dataField: "validFrom",
      text: translator("DigitalContent-ValidFrom"),
      headerStyle: () => ({ width: "130px" }),
      formatter: dateFormatter,
    },
    {
      dataField: "validTo",
      text: translator("DigitalContent-ValidTo"),
      headerStyle: () => ({ width: "130px" }),
      formatter: dateFormatter,
    },
  ];

  const rowEvents = {
    onClick: (e, row) => {
      e.stopPropagation();
      setUpdateData(row);
    },
  };

  const confirmDelete = (id) => {
    digitalContentService
      .remove(id)
      .then(() => {
        setActionModal({ ...actionModal, show: false });
        setResultModal({
          ...resultModal,
          title: translator("DigitalContent-ResultModal-DeleteSuccess-Title"),
          text: translator("DigitalContent-ResultModal-DeleteSuccess-Text"),
          show: true,
          btnClass: "btn btn-primary",
        });
        setUpdate(new Date());
      })
      .catch(() =>
        setResultModal({
          ...resultModal,
          title: translator("DigitalContent-ResultModal-DeleteSuccess-Title"),
          text: translator("DigitalContent-ResultModal-DeleteUnSuccess-Text"),
          show: true,
          btnClass: "btn btn-primary",
        })
      );
  };

  return (
    <div className="p-2 pt-3">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("DigitalContent-View-Subtitle")}</span>
        </div>
        <div className="card-body">
          <ToolkitProvider
            keyField="digitalContentId"
            data={data}
            columns={columns}
            search
          >
            {(props) => (
              <>
                <div className="row">
                  <div className="col-md-6 col-sm-12 mt-2" onClick={() => setUpdateData(null)}>
                    {isLoading ? (
                      <div className="dashboard-card-spinner">
                        <SpinnerLoading />
                      </div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-end">
                          <div>
                            <SearchBar {...props.searchProps} />
                          </div>
                        </div>
                        <BootstrapTable
                          {...props.baseProps}
                          rowClasses="boostrap-table-row"
                          rowEvents={rowEvents}
                          noDataIndication={translator("No-Data")}
                          pagination={paginationFactory({})}
                        />
                      </>
                    )}
                  </div>

                  <div className="col-md-6 col-sm-12 mt-2">
                    {!updateData && (
                      <AddDigitalContent
                        onSuccess={() => {
                          setUpdate(new Date());
                          setUpdateData(null);
                        }}
                        disabled={!isActionAllowed}
                        setResultModal={setResultModal}
                      />
                    )}
                    {updateData && (
                      <UpdateDigitalContent
                        updateData={updateData}
                        disabled={!isActionAllowed}
                        onSuccess={() => {
                          setUpdate(new Date());
                          setUpdateData(null);
                        }}
                        setResultModal={setResultModal}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </ToolkitProvider>
        </div>
      </div>

      <ResultModal
        show={resultModal.show}
        title={resultModal.title}
        text={resultModal.text}
        btnClass={resultModal.btnClass}
        handleClose={() => {
          setResultModal({ ...resultModal, show: false });
          setUpdateData(null);
        }}
      />

      <ActionModal
        show={actionModal.show}
        handleHide={() => {
          setActionModal({ ...actionModal, show: false });
        }}
        title={actionModal.title}
        text={actionModal.text}
        confirmBtn={actionModal.confirmBtn}
        confirmAction={actionModal.confirmAction}
      />
    </div>
  );
};

export default Index;
